import { Role } from "~/enums/role";
import type { LocationIdentifier, UserProfile } from "~/types/api";
import { locationIdsMatch } from "~/utils/locationIdentifier";

export type PatientContextSelectionDecision =
  | { type: "none" }
  | { type: "switcher" }
  | { type: "select-patient"; locationId: LocationIdentifier };

export interface PatientContextSelectionInput {
  profiles: UserProfile[];
  currentLocationId: LocationIdentifier | null | undefined;
  routeLocationId?: LocationIdentifier;
}

export const filterPatientProfiles = (profiles: UserProfile[]): UserProfile[] =>
  profiles.filter((profile) => profile.roles.includes(Role.Patient));

const profileHasLocation = (profile: UserProfile, locationId: LocationIdentifier): boolean =>
  locationIdsMatch(profile.location.id, locationId);

export function decidePatientContextSelection(
  input: PatientContextSelectionInput,
): PatientContextSelectionDecision {
  const { currentLocationId, routeLocationId } = input;
  const eligibleProfiles = filterPatientProfiles(input.profiles);
  const onlyProfile = eligibleProfiles.length === 1 ? eligibleProfiles[0] : undefined;

  if (
    routeLocationId !== undefined &&
    eligibleProfiles.some((profile) => profileHasLocation(profile, routeLocationId))
  ) {
    return { type: "select-patient", locationId: routeLocationId };
  }

  if (
    currentLocationId !== null &&
    currentLocationId !== undefined &&
    eligibleProfiles.some((profile) => profileHasLocation(profile, currentLocationId))
  ) {
    return { type: "none" };
  }

  if (onlyProfile) {
    return { type: "select-patient", locationId: Number(onlyProfile.location.id) };
  }

  return eligibleProfiles.length > 1 ? { type: "switcher" } : { type: "none" };
}
